import React, { useEffect, useMemo, useState } from 'react';
import { setAnalyticsConsent } from '../utils/gtag';

type ConsentPrefs = {
  analytics: boolean;
  ads: boolean;
  consentedAt: string;
  gpc?: boolean;
};

const STORAGE_KEY = 'loconvo_consent_v1';

function readPrefs(): ConsentPrefs | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePrefs(prefs: ConsentPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore storage failures
  }
  (window as any).__loconvoConsent = prefs;
}

const ConsentBanner: React.FC = () => {
  const initial = useMemo(() => readPrefs(), []);
  const [open, setOpen] = useState(!initial);
  const [analytics, setAnalytics] = useState<boolean>(initial?.analytics ?? false);
  const [ads, setAds] = useState<boolean>(initial?.ads ?? false);
  const [expanded, setExpanded] = useState(false);

  // Honor Global Privacy Control by defaulting to opt-out when detected
  useEffect(() => {
    const gpc = typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;
    const stored = readPrefs();
    if (gpc && (!stored || stored.analytics || stored.ads)) {
      const prefs: ConsentPrefs = { analytics: false, ads: false, consentedAt: new Date().toISOString(), gpc: true };
      writePrefs(prefs);
      setAnalyticsConsent(false);
      setOpen(false);
      setAnalytics(false);
      setAds(false);
    }
    if (stored?.analytics) {
      setAnalyticsConsent(true);
    }
  }, []);

  const save = (vals: { analytics: boolean; ads: boolean }) => {
    const prefs: ConsentPrefs = { ...vals, consentedAt: new Date().toISOString() };
    writePrefs(prefs);
    setAnalytics(prefs.analytics);
    setAds(prefs.ads);
    setAnalyticsConsent(prefs.analytics);
    setOpen(false);
    setExpanded(false);
  };

  const handleReject = () => save({ analytics: false, ads: false });
  const handleAcceptAll = () => save({ analytics: true, ads: true });
  const handleSave = () => save({ analytics, ads });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Privacy and cookie consent"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: '100%',
          background: '#0f172a',
          color: 'white',
          padding: '16px 18px',
          borderRadius: 12,
          boxShadow: '0 15px 45px rgba(0,0,0,0.3)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Privacy & cookies</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: '#e5e7eb' }}>
              We use cookies/local storage to run the site. With your permission, we use Google Analytics for product metrics and Google AdSense for ads.
              In some regions, these only run after you consent. You can opt out of personalized ads and analytics.
            </div>
            {expanded && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#cbd5e1', lineHeight: 1.45 }}>
                We honor Global Privacy Control (GPC) and “Do Not Sell/Share” choices where required. You can change your selection anytime.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.35)',
              color: 'white',
              borderRadius: 8,
              padding: '10px 12px',
              cursor: 'pointer',
              fontWeight: 600,
              minWidth: 110
            }}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
        </div>

        {expanded && (
          <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5e7eb', fontSize: 14 }}>
              <input type="checkbox" checked={analytics} onChange={e => setAnalytics(e.target.checked)} />
              Allow analytics (Google Analytics)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5e7eb', fontSize: 14 }}>
              <input type="checkbox" checked={ads} onChange={e => setAds(e.target.checked)} />
              Allow personalized ads (Google AdSense)
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleReject}
            style={{
              background: 'white',
              color: '#0f172a',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              padding: '10px 14px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={expanded ? handleSave : handleAcceptAll}
            style={{
              background: '#38bdf8',
              color: '#0f172a',
              border: '1px solid #0ea5e9',
              borderRadius: 10,
              padding: '10px 14px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {expanded ? 'Save choices' : 'Accept all'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
